'use client';

import { c as _c } from "react/compiler-runtime";
import Link from 'next/link';
import Image from 'next/image';
import { useState, useCallback, useEffect } from 'react';
import Modal from 'react-modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faBookmark, faXmark } from '@awesome.me/kit-94e593bf23/icons/classic/light';
import { faMagnifyingGlassPlus } from '@awesome.me/kit-94e593bf23/icons/classic/regular';
import { faHiveIconSmallLight } from '@awesome.me/kit-94e593bf23/icons/kit/custom';
import { decodeEntities } from '@/app/_lib/services/ClientUtil';
import Price from '@/app/_components/Price';
import QuantityInput from '@/app/_components/QuantityInput';
import ProductFavoriteMarker from '@/app/_components/ProductFavoriteMarker';
import { useGlobalMessage } from '@/app/_lib/hooks';
import { useConfirmDialog } from '@/app/_lib/_context';
import { trackRemoveFromCart, gaItemFromCartItem } from '@/app/_lib/analytics/ga4';

// Format unix timestamp to readable date
const formatBackorderDate = timestamp => {
  if (!timestamp) return null;
  const date = new Date(timestamp * 1000);
  return date.toLocaleDateString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric'
  });
};
const CartOptions = t0 => {
  const $ = _c(2);
  const {
    options
  } = t0;
  if (!options) {
    return null;
  }
  const now = Date.now() / 1000;
  let t1;
  if ($[0] !== options) {
    t1 = <>{Object.entries(options).map((option, index) => {
        const opt = option[1];
        const hasUpcharge = opt.option_price > 0 || opt.original_option_price > 0;
        const isDiscounted = opt.original_option_price > 0 && opt.original_option_price > opt.option_price;
        let optionBackorderMessage = null;
        if (opt.production_hold_expiration_date && opt.production_hold_expiration_date > now) {
          optionBackorderMessage = {
            prompt: "production hold",
            message: `estimated availability ${formatBackorderDate(opt.production_hold_expiration_date)}`
          };
        } else {
          if (opt.backorder_expiration_date && opt.backorder_expiration_date > now) {
            optionBackorderMessage = {
              prompt: "backordered",
              message: `estimated in stock ${formatBackorderDate(opt.backorder_expiration_date)}`
            };
          }
        }
        return <div key={index} className="capitalize mb-2 text-xs"><span className="text-ink-light">{opt.selection_label}:</span><span className="font-medium ml-1">{opt.option_name}</span>{hasUpcharge && <span className="ml-1">{isDiscounted && <span className="font-extralight line-through text-ink-light mr-1">+ <Price amount={opt.original_option_price} /></span>}<span className="font-medium text-black">+ <Price amount={opt.option_price} /></span></span>}{opt.option_photo && <div className="mt-1"><Image src={`${process.env.NEXT_PUBLIC_RESOURCE_URL}/option_photos/${opt.option_photo}`} alt={opt.option_name} width={40} height={25} className="shadow-sm" /></div>}{optionBackorderMessage && <div className="mt-1"><span className="text-red">{optionBackorderMessage.prompt}</span> {optionBackorderMessage.message}</div>}</div>;
      })}</>;
    $[0] = options;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
const SwatchImageModal = t0 => {
  const $ = _c(8);
  const {
    swatch,
    isOpen,
    onClose
  } = t0;
  const t1 = swatch ? `${swatch.swatch_name} swatch image` : "Swatch image";
  let t2;
  if ($[0] !== onClose || $[1] !== swatch) {
    t2 = swatch && <><button onClick={onClose} className="absolute -top-3 -right-3 z-10 w-8 h-8 flex items-center justify-center bg-white rounded-full shadow-lg text-dark hover:text-primary transition-colors" aria-label="Close"><FontAwesomeIcon icon={faXmark} className="text-lg" /></button><div className="p-2">{swatch.swatch_photo_large_url ? <Image src={`${process.env.NEXT_PUBLIC_RESOURCE_URL}/${swatch.swatch_photo_large_url}`} alt={swatch.swatch_name} width={500} height={500} className="w-full h-auto" /> : <div className="flex flex-col items-center justify-center aspect-square w-full max-w-[500px]"><Image src={`${process.env.NEXT_PUBLIC_RESOURCE_URL}/swatch_photos/${swatch.swatch_photo}`} alt={swatch.swatch_name} width={200} height={200} /><p className="text-xs text-light mt-2">Large image not available</p></div>}</div><div className="p-3 flex items-end justify-between"><div>{swatch.swatch_set_label && <div className="text-xs text-light mb-0.5">{swatch.swatch_set_label}</div>}<div className="text-xs font-medium">{swatch.swatch_name}{swatch.swatch_code && <span className="pl-0.5 text-light font-normal">({swatch.swatch_code})</span>}</div></div><FontAwesomeIcon icon={faHiveIconSmallLight} className="text-primary text-3xl mb-0.5" /></div></>;
    $[0] = onClose;
    $[1] = swatch;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== isOpen || $[4] !== onClose || $[5] !== t1 || $[6] !== t2) {
    t3 = <Modal isOpen={isOpen} onRequestClose={onClose} bodyOpenClassName="ReactModal__Body--open" htmlOpenClassName="ReactModal__Html--open" contentLabel={t1} overlayClassName="fixed inset-0 z-[1055] flex items-center justify-center bg-modal-overlay" className="relative bg-white rounded-lg w-[calc(100%-60px)] max-w-[500px] max-h-[90vh] outline-none">{t2}</Modal>;
    $[3] = isOpen;
    $[4] = onClose;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
};
const CartSwatches = t0 => {
  const $ = _c(9);
  const {
    swatches
  } = t0;
  const [viewingSwatch, setViewingSwatch] = useState(null);
  if (!swatches) {
    return null;
  }
  const now = Date.now() / 1000;
  let t1;
  if ($[0] !== swatches) {
    t1 = Object.entries(swatches).map((swatch, index) => {
      const sw = swatch[1];
      const swatchPrice = sw.swatch_price || sw.price || 0;
      const originalPrice = sw.original_swatch_price || sw.original_price || 0;
      const hasUpcharge = swatchPrice > 0 || originalPrice > 0;
      const isDiscounted = originalPrice > 0 && originalPrice > swatchPrice;
      let swatchBackorderMessage = null;
      if (sw.production_hold_expiration_date && sw.production_hold_expiration_date > now) {
        swatchBackorderMessage = {
          prompt: "production hold",
          message: `estimated availability ${formatBackorderDate(sw.production_hold_expiration_date)}`
        };
      } else {
        if (sw.backorder_expiration_date && sw.backorder_expiration_date > now) {
          swatchBackorderMessage = {
            prompt: "backordered",
            message: `estimated in stock ${formatBackorderDate(sw.backorder_expiration_date)}`
          };
        }
      }
      return <div key={index} className="capitalize mb-2 text-xs"><span className="text-ink-light">{sw.swatch_set_label}:</span><span className="font-medium ml-1">{sw.swatch_name}{sw.swatch_code && <span className="font-normal text-ink-light ml-0.5">({sw.swatch_code})</span>}</span>{hasUpcharge && <span className="ml-1">{isDiscounted && <span className="font-extralight line-through text-ink-light mr-1">+ <Price amount={originalPrice} /></span>}<span className="font-medium text-black">+ <Price amount={swatchPrice} /></span></span>}{sw.swatch_photo && <button onClick={() => setViewingSwatch(sw)} className="mt-1 flex items-center gap-2 font-extralight text-ink-light hover:text-primary transition-colors" title="View larger"><Image src={`${process.env.NEXT_PUBLIC_RESOURCE_URL}/swatch_photos/${sw.swatch_photo}`} alt={sw.swatch_name} width={40} height={40} className="shadow-sm" /><FontAwesomeIcon icon={faMagnifyingGlassPlus} className="text-xs" /><span>View</span></button>}{swatchBackorderMessage && <div className="mt-1"><span className="text-red">{swatchBackorderMessage.prompt}</span> {swatchBackorderMessage.message}</div>}</div>;
    });
    $[0] = swatches;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = !!viewingSwatch;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => setViewingSwatch(null);
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== t2 || $[4] !== viewingSwatch) {
    t4 = <SwatchImageModal swatch={viewingSwatch} isOpen={t2} onClose={t3} />;
    $[3] = t2;
    $[4] = viewingSwatch;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t1 || $[7] !== t4) {
    t5 = <>{t1}{t4}</>;
    $[6] = t1;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return t5;
};
const CartItem = ({
  cartItem,
  itemIndex,
  deleteCartItem,
  updateCartItem,
  saveCartItemForLater,
  isTradeCustomer,
  promotion
}) => {
  useEffect(() => {
    Modal.setAppElement('#hivenext');
  }, []);
  const addGlobalMessage = useGlobalMessage();
  const {
    confirm
  } = useConfirmDialog();
  const setQuantity = useCallback(quantity => {
    cartItem.quantity = quantity;
    updateCartItem(itemIndex, cartItem);
  }, [cartItem, itemIndex, updateCartItem]);
  const getImageForItem = cartItem_0 => {
    // Priority: variant > option_product_photo > thumb_1 > product_photo
    // cart_swatch_image is shown separately as swatch indicator, not as product photo
    if (cartItem_0.product?.variant?.file_name) {
      return cartItem_0.product.variant.file_name;
    }
    if (cartItem_0.product?.option_product_photo) {
      return `option_photos/${cartItem_0.product.option_product_photo}`;
    }
    if (cartItem_0.product?.thumb_1) {
      return `thumbs/${cartItem_0.product.thumb_1}`;
    }
    return cartItem_0.product?.product_photo || '';
  };
  const handleDeleteCartItemClick = async (event, itemIndex_0, cartItem_1) => {
    const result = await confirm({
      title: 'Remove from Cart?',
      message: `What would you like to do with "${decodeEntities(cartItem_1.product.sub_product_name)}"?`,
      buttons: [{
        label: 'Remove from Cart',
        action: 'remove',
        variant: 'primary'
      }, {
        label: 'Save for Later',
        action: 'save',
        variant: 'secondary'
      }]
    });
    if (result === 'remove') {
      // GA4 remove_from_cart
      trackRemoveFromCart({
        value: cartItem_1.total_price,
        items: [gaItemFromCartItem(cartItem_1, itemIndex_0)]
      });
      deleteCartItem(itemIndex_0);
      addGlobalMessage({
        message: 'Item removed from cart',
        messageType: 'success'
      });
    } else if (result === 'save') {
      saveCartItemForLater(itemIndex_0);
      addGlobalMessage({
        message: `${decodeEntities(cartItem_1.product.sub_product_name)} was saved for later`,
        messageType: 'success'
      });
    }
    // result === null means cancelled - do nothing
  };
  const handleSaveCartItemForLaterClick = (event_0, itemIndex_1, cartItem_2) => {
    saveCartItemForLater(itemIndex_1);
    const message = decodeEntities(cartItem_2.product.sub_product_name) + ' was added to saved for later';
    addGlobalMessage({
      message: message,
      messageType: 'success'
    });
  };
  return <div className="pb-6 mb-6 border-b border-gray-6">
            {/* Mobile Layout (below sm) */}
            <div className="flex gap-4 sm:hidden">
                {/* Left column: Image + Actions */}
                <div className="shrink-0 w-[140px]">
                    <Link href={`${cartItem.product?.url}`} title={decodeEntities(cartItem.product.sub_product_name)} className="block relative aspect-square overlay-block bg-white mb-3">
                        <Image src={`${process.env.NEXT_PUBLIC_RESOURCE_URL}/${getImageForItem(cartItem)}`} alt={decodeEntities(cartItem.product.sub_product_name)} className="h-auto p-2" style={{
            objectFit: 'contain'
          }} fill={true} quality={90} sizes="140px" />
                    </Link>
                    {/* Actions below image */}
                    <div className="text-xs space-y-2">
                        <button onClick={event_1 => handleDeleteCartItemClick(event_1, itemIndex, cartItem)} title="Remove from your cart" className="flex items-center hover:text-primary">
                            <FontAwesomeIcon icon={faTrash} className="mr-1.5" />
                            <span>Remove</span>
                        </button>
                        <button onClick={event_2 => handleSaveCartItemForLaterClick(event_2, itemIndex, cartItem)} title="Save for later" className="flex items-center hover:text-primary">
                            <FontAwesomeIcon icon={faBookmark} className="mr-1.5" />
                            <span>Save for later</span>
                        </button>
                        <ProductFavoriteMarker sub_product_id={cartItem.product?.sub_product_id} isVisible={true} className="flex items-center hover:text-primary" label="Favorite" labelPosition="after" productName={decodeEntities(cartItem.product?.sub_product_name)} />
                    </div>
                </div>

                {/* Right column: Product details */}
                <div className="flex-1 min-w-0">
                    {/* Name */}
                    <div className="mb-2">
                        <Link href={`${cartItem.product?.url}`} title={decodeEntities(cartItem.product.sub_product_name)} className="font-medium text-sm leading-tight capitalize text-ink-dark">
                            {decodeEntities(cartItem.product.sub_product_name)}
                        </Link>
                        {(cartItem.product?.designer_url || cartItem.product?.supplier_url) && <span className="block text-light text-3xs mt-0.5">
                                {cartItem.product?.designer_name && cartItem.product?.designer_url && <>By <Link href={cartItem.product.designer_url} className="hover:text-primary transition-colors duration-200">{cartItem.product.designer_name}</Link></>}
                                {cartItem.product?.designer_url && cartItem.product?.supplier_url && ', '}
                                {cartItem.product?.supplier_name && cartItem.product?.supplier_url && <>From <Link href={cartItem.product.supplier_url} className="hover:text-primary transition-colors duration-200">{cartItem.product.supplier_name}</Link></>}
                            </span>}
                        {/* Product-level backorder message */}
                        {cartItem.product?.custom_backorder_message ? <div className="text-xs mt-1">
                                <span className="text-red">{cartItem.product.backorder_hold_message}</span> {cartItem.product.backorder_message}
                            </div> : cartItem.product?.backordered && !cartItem.product?.in_inventory && cartItem.product?.backorder_message && <div className="text-xs mt-1">
                                <span className="text-red">backordered</span> {cartItem.product.backorder_message}
                            </div>}
                    </div>

                    {/* Price */}
                    <div className="text-lg font-medium mb-1"><Price amount={cartItem.total_price} /></div>
                    {cartItem.original_total > cartItem.total_price && <div className="text-sm mb-1">
                            <Price amount={cartItem.original_total} className="line-through text-light" />
                        </div>}
                    {cartItem.product?.on_sale == 1 && cartItem.product?.discount_percentage > 0 && <div className="text-red text-sm font-medium mb-1">{cartItem.product.discount_percentage}% off</div>}
                    {/* Show ineligibility message on sale items when promotion is active */}
                    {promotion && !promotion.requires_login && cartItem.product?.on_sale == 1 && <div className="text-red text-xs italic mb-1">This product is already discounted and ineligible for the promotion code discount.</div>}
                    {isTradeCustomer && cartItem.trade_price !== undefined && <div className="text-sm text-light mb-3">
                            Trade Price <Price amount={cartItem.trade_price} />
                        </div>}

                    {/* Quantity */}
                    <div className="mb-4">
                        <QuantityInput quantity={cartItem.quantity} setQuantity={setQuantity} />
                    </div>

                    {/* Base Price */}
                    <div className="mb-2 text-xs">
                        <span className="text-ink-light">Base Price:</span>
                        <Price amount={cartItem.product?.retail_price} className="font-medium ml-1" />
                        {cartItem.product?.original_price && <Price amount={cartItem.product.original_price} className="line-through text-light ml-1" />}
                    </div>

                    {/* Shipping */}
                    <div className="mb-3 text-xs">
                        <span className="text-ink-light">Shipping:</span>
                        <span className="font-medium ml-1">{cartItem.shipping?.cart_message}</span>
                    </div>

                    {/* Lead Time */}
                    {cartItem.product?.lead_time && <div className="mb-3 text-xs capitalize">
                            {cartItem.product.lead_time}
                        </div>}

                    {/* Options & Swatches */}
                    <CartOptions options={cartItem.product.options} />
                    <CartSwatches swatches={cartItem.product.swatches} />
                </div>
            </div>

            {/* Desktop Layout (sm and up) - 3 columns */}
            <div className="hidden sm:grid sm:grid-cols-[100px_1fr_auto] sm:gap-6">
                {/* Column 1: Image */}
                <Link href={`${cartItem.product?.url}`} title={decodeEntities(cartItem.product.sub_product_name)} className="block relative aspect-square overlay-block bg-white">
                    <Image src={`${process.env.NEXT_PUBLIC_RESOURCE_URL}/${getImageForItem(cartItem)}`} alt={decodeEntities(cartItem.product.sub_product_name)} className="h-auto p-2" style={{
          objectFit: 'contain'
        }} fill={true} quality={90} sizes="100px" />
                </Link>

                {/* Column 2: Product Info */}
                <div className="min-w-0">
                    {/* Name */}
                    <div className="mb-3">
                        <Link href={`${cartItem.product?.url}`} title={decodeEntities(cartItem.product.sub_product_name)} className="font-medium text-sm capitalize text-ink-dark">
                            {decodeEntities(cartItem.product.sub_product_name)}
                        </Link>
                        {(cartItem.product?.designer_url || cartItem.product?.supplier_url) && <span className="block text-light text-3xs mt-0.5">
                                {cartItem.product?.designer_name && cartItem.product?.designer_url && <>By <Link href={cartItem.product.designer_url} className="hover:text-primary transition-colors duration-200">{cartItem.product.designer_name}</Link></>}
                                {cartItem.product?.designer_url && cartItem.product?.supplier_url && ', '}
                                {cartItem.product?.supplier_name && cartItem.product?.supplier_url && <>From <Link href={cartItem.product.supplier_url} className="hover:text-primary transition-colors duration-200">{cartItem.product.supplier_name}</Link></>}
                            </span>}
                        {/* Product-level backorder message */}
                        {cartItem.product?.custom_backorder_message ? <div className="text-xs mt-1">
                                <span className="text-red">{cartItem.product.backorder_hold_message}</span> {cartItem.product.backorder_message}
                            </div> : cartItem.product?.backordered && !cartItem.product?.in_inventory && cartItem.product?.backorder_message && <div className="text-xs mt-1">
                                <span className="text-red">backordered</span> {cartItem.product.backorder_message}
                            </div>}
                    </div>

                    {/* Quantity */}
                    <div className="mb-4">
                        <QuantityInput quantity={cartItem.quantity} setQuantity={setQuantity} />
                    </div>

                    {/* Base Price */}
                    <div className="mb-2 text-xs">
                        <span className="text-ink-light">Base Price:</span>
                        <Price amount={cartItem.product?.retail_price} className="font-medium ml-1" />
                        {cartItem.product?.original_price && <Price amount={cartItem.product.original_price} className="line-through text-light ml-1" />}
                    </div>

                    {/* Shipping */}
                    <div className="mb-3 text-xs">
                        <span className="text-ink-light">Shipping:</span>
                        <span className="font-medium ml-1">{cartItem.shipping?.cart_message}</span>
                    </div>

                    {/* Lead Time */}
                    {cartItem.product?.lead_time && <div className="mb-3 text-xs capitalize">
                            {cartItem.product.lead_time}
                        </div>}

                    {/* Options & Swatches */}
                    <CartOptions options={cartItem.product.options} />
                    <CartSwatches swatches={cartItem.product.swatches} />
                </div>

                {/* Column 3: Price + Actions */}
                <div className="text-right min-w-[120px]">
                    {/* Price */}
                    <div className="font-medium text-lg mb-1"><Price amount={cartItem.total_price} /></div>
                    {cartItem.original_total > cartItem.total_price && <div className="line-through text-light text-sm mb-1"><Price amount={cartItem.original_total} /></div>}
                    {cartItem.product?.on_sale == 1 && cartItem.product?.discount_percentage > 0 && <div className="text-red text-sm font-medium mb-1">{cartItem.product.discount_percentage}% off</div>}
                    {/* Show ineligibility message on sale items when promotion is active */}
                    {promotion && !promotion.requires_login && cartItem.product?.on_sale == 1 && <div className="text-red text-xs italic mb-1">This product is already discounted and ineligible for the promotion code discount.</div>}
                    {isTradeCustomer && cartItem.trade_price !== undefined && <div className="text-sm text-light mb-2">
                            Trade Price <Price amount={cartItem.trade_price} />
                        </div>}

                    {/* Actions */}
                    <div className="text-xs space-y-2 mt-3">
                        <button onClick={event_3 => handleDeleteCartItemClick(event_3, itemIndex, cartItem)} title="Remove from your cart" className="flex items-center justify-end w-full hover:text-primary">
                            <span>Remove</span>
                            <FontAwesomeIcon icon={faTrash} className="ml-1.5" />
                        </button>
                        <button onClick={event_4 => handleSaveCartItemForLaterClick(event_4, itemIndex, cartItem)} title="Save for later" className="flex items-center justify-end w-full hover:text-primary">
                            <span>Save for later</span>
                            <FontAwesomeIcon icon={faBookmark} className="ml-1.5" />
                        </button>
                        <ProductFavoriteMarker sub_product_id={cartItem.product?.sub_product_id} isVisible={true} className="flex items-center justify-end w-full hover:text-primary" label="Favorite" labelPosition="before" productName={decodeEntities(cartItem.product?.sub_product_name)} />
                    </div>
                </div>
            </div>
        </div>;
};
export default CartItem;